<script setup lang="ts">
/**
 * The Pinch Crew logo.
 *
 * Exists because every app had hand-rolled its own: a `<span>` holding the
 * single letter "N" — for Nokijo — with the same 6 lines of CSS copied into
 * six files. The rename could not find them, because a lone letter is not a
 * searchable string, and the signup page had quietly drifted into having no
 * mark at all. One component means the next rebrand is one file.
 *
 * The glyph matches the landing page: a rounded square with a "P" and a dot.
 */
withDefaults(defineProps<{
  /** Logo only, or logo plus wordmark. */
  showName?: boolean
  /** `sm` for a sidebar, `md` for an auth screen. */
  size?: 'sm' | 'md'
  /** Overrides the wordmark, e.g. "Pinch Crew Ops". */
  name?: string
}>(), {
  showName: true,
  size: 'md',
  name: 'Pinch Crew',
})
</script>

<template>
  <span class="brand" :class="`brand--${size}`">
    <svg
      class="brand__mark" viewBox="0 0 26 26" fill="none"
      role="img" :aria-label="showName ? undefined : name"
      :aria-hidden="showName ? 'true' : undefined"
    >
      <rect width="26" height="26" rx="7" fill="var(--k-color-primary)" />
      <path
        d="M7.5 17.5V8.6c0-.3.2-.6.6-.6h3.6a3.2 3.2 0 1 1 0 6.4H9.3"
        stroke="var(--k-color-text-inverse)" stroke-width="2.1"
        stroke-linecap="round" stroke-linejoin="round"
      />
      <circle cx="17.4" cy="16.6" r="1.6" fill="var(--k-color-text-inverse)" />
    </svg>
    <span v-if="showName" class="brand__name">{{ name }}</span>
  </span>
</template>

<style scoped>
.brand { display: inline-flex; align-items: center; gap: var(--k-space-2); }

.brand__mark { display: block; flex: none; }
.brand--sm .brand__mark { width: 28px; height: 28px; }
.brand--md .brand__mark { width: 36px; height: 36px; }

.brand__name {
  font-weight: var(--k-font-weight-bold);
  letter-spacing: -0.02em;
  color: var(--k-color-text);
}
.brand--sm .brand__name { font-size: var(--k-font-size-md); }
.brand--md .brand__name { font-size: var(--k-font-size-xl); }
</style>
